/**
 * Indications -- Participant-side IOI dashboard.
 *
 * Sprint C-LSP-02 Wave C-FE.
 *
 * Lists the caller FI's IOIs across the Loan Participations network. Surfaces
 * the lifecycle states (SUBMITTED, ALLOCATION_OFFERED, ACCEPTED,
 * DECLINED, WITHDRAWN, EXPIRED) and routes the participant to the
 * accept/decline action dialogs for ALLOCATION_OFFERED rows.
 *
 * Route: /syndication/indications.
 *
 * Backend: GET /api/v1/syndication/iois/me. Server-side scoping
 * returns own-only IOIs; the SPA does not filter on participantId.
 *
 * Persona gating (F-INDICATIONS-PERSONA-GATE, 2026-05-15):
 * /iois/me requires a CRT participant identity. Platform-admin SSO
 * tokens (aaim_super_admin / platform_admin / e2e_test_admin) carry
 * no participantId, so the route 401s and the SPA's openapi-fetch
 * authMiddleware bounces to /login -- breaking the entire portal for
 * platform operators who navigate to /syndication/indications. The
 * /iois/me query is gated to FI-persona roles via the auth store's
 * hasAnyRole helper (case-insensitive per
 * .claude/rules/authz-case-insensitive.md). Platform admins receive a
 * persona-appropriate informational state instead.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import {
  Heading,
  Card,
  Badge,
  Button,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
  Dialog,
  Input,
  FormLabel,
  FormHelperText,
  DataGrid,
  Checkbox,
  Textarea,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import { type EligibilityReason, ELIGIBILITY_SEVERITY_LABEL } from './eligibility';
import {
  formatCurrencyFull,
  formatCurrencyCompact as formatCurrency,
  formatDate,
  IOI_STATUS_LABELS,
} from './format';
import { toSyndicationReadError } from './read-error';
import { ReadErrorBanner } from './ReadErrorBanner';

/**
 * FI-tenant operator roles whose Bearer JWTs carry a participantId
 * resolvable by CRT. Only these personas can hold IOIs and therefore
 * read /api/v1/syndication/iois/me. Platform admins are intentionally
 * absent: they have no participant identity in CRT and the route 401s
 * for them.
 *
 * Lowercase per the platform-wide normalization tenet
 * (.claude/rules/authz-case-insensitive.md). The auth store's
 * hasAnyRole helper lowercases both sides before comparing, so this
 * literal list also doubles as documentation of the FI persona set.
 */
// Apex Aaim platform admins (aaim_super_admin / e2e_test_admin) are
// treated as FI personas here because Aaim Inc. is itself an FI tenant
// in the model -- apex resolves to APEX_TENANT_ID and operates against
// the Aaim demo tenant exactly like any other FI for IOIs and the
// Loan Participations network. The OrgSwitcher remains the entry point
// for impersonating a different FI; until then the apex admin sees
// Aaim Inc.'s own IOI book rather than a "switch tenant" nag.
const FI_PERSONA_ROLES = [
  'fi_admin',
  'fi_loan_officer',
  'fi_compliance',
  'aaim_super_admin',
  'e2e_test_admin',
] as const;

type ParticipationIoi = LspComponents['schemas']['ParticipationIoi'];

function getIoiStatusVariant(
  status: ParticipationIoi['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'SUBMITTED':
      return 'info';
    case 'ALLOCATION_OFFERED':
      return 'warning';
    case 'ACCEPTED':
      return 'success';
    case 'DECLINED':
      return 'neutral';
    case 'WITHDRAWN':
      return 'neutral';
    case 'EXPIRED':
      return 'error';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useMyIois(enabled: boolean) {
  return useQuery({
    queryKey: ['lsp-mine-iois'],
    queryFn: async () => {
      const { data, error, response } = await loanSyndicationClient.GET(
        '/api/v1/syndication/iois/me',
        {
          params: { query: { page: 1, pageSize: 100 } },
        },
      );
      // Read before the error narrowing: on a route whose contract declares
      // no error body, openapi-fetch types that branch as never.
      const httpStatus = response.status;
      if (error) {
        throw toSyndicationReadError(
          error,
          httpStatus,
          'Failed to load indications of interest',
        );
      }
      return data;
    },
    enabled,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/**
 * Client-side mirror of the server's ELIGIBILITY_BLOCKED 422: the accept was
 * refused because the buyer's verdict at the allocated amount is INELIGIBLE.
 * Carries the computed reasons so the dialog can render exactly what an
 * override attestation would be attesting against.
 */
class EligibilityBlockedClientError extends Error {
  constructor(message: string, public readonly reasons: EligibilityReason[]) {
    super(message);
    this.name = 'EligibilityBlockedClientError';
  }
}

function parseEligibilityBlocked(error: unknown): EligibilityBlockedClientError | null {
  const body = error as {
    error?: string;
    message?: string;
    details?: { eligibility?: { reasons?: EligibilityReason[] } };
  };
  if (body?.error !== 'ELIGIBILITY_BLOCKED') return null;
  return new EligibilityBlockedClientError(
    body.message ?? 'Commitment blocked by the eligibility gate',
    body.details?.eligibility?.reasons ?? [],
  );
}

function useAcceptIoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      ioiId: string;
      idempotencyKey: string;
      eligibilityOverride?: { acknowledged: boolean; justification: string };
    }) => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/iois/{ioiId}/accept',
        {
          params: { path: { ioiId: vars.ioiId } },
          body: {
            idempotencyKey: vars.idempotencyKey,
            eligibilityOverride: vars.eligibilityOverride,
          },
        },
      );
      if (error) {
        const blocked = parseEligibilityBlocked(error);
        if (blocked) throw blocked;
        throw new Error(
          (error as { message?: string })?.message ?? 'Allocation accept failed',
        );
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lsp-mine-iois'] });
      void queryClient.invalidateQueries({ queryKey: ['lsp-commitments'] });
    },
  });
}

function useDeclineIoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { ioiId: string; reason?: string }) => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/iois/{ioiId}/decline',
        {
          params: { path: { ioiId: vars.ioiId } },
          body: { reason: vars.reason },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Allocation decline failed',
        );
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lsp-mine-iois'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Accept dialog
// ---------------------------------------------------------------------------

function AcceptDialog({
  ioi,
  open,
  onClose,
}: {
  ioi: ParticipationIoi | null;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useAcceptIoi();
  // Override attestation state, entered only after the server's eligibility
  // gate returns ELIGIBILITY_BLOCKED. The idempotency key is minted once per
  // dialog session and REUSED for the override resubmit, so the two attempts
  // can never double-bind.
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [overrideAcknowledged, setOverrideAcknowledged] = useState(false);
  const [overrideJustification, setOverrideJustification] = useState('');

  const blocked = mutation.error instanceof EligibilityBlockedClientError
    ? mutation.error
    : null;
  const justificationValid = overrideJustification.trim().length >= 20;

  function reset() {
    mutation.reset();
    setIdempotencyKey(null);
    setOverrideAcknowledged(false);
    setOverrideJustification('');
    onClose();
  }

  function mintOrReuseKey(): string {
    if (idempotencyKey) return idempotencyKey;
    const key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `accept-${ioi?.ioiId}-${Date.now()}`;
    setIdempotencyKey(key);
    return key;
  }

  function handleConfirm() {
    if (!ioi) return;
    mutation.mutate(
      { ioiId: ioi.ioiId, idempotencyKey: mintOrReuseKey() },
      {
        onSuccess: () => {
          setTimeout(reset, 1500);
        },
      },
    );
  }

  function handleOverrideConfirm() {
    if (!ioi || !overrideAcknowledged || !justificationValid) return;
    mutation.mutate(
      {
        ioiId: ioi.ioiId,
        idempotencyKey: mintOrReuseKey(),
        eligibilityOverride: {
          acknowledged: true,
          justification: overrideJustification.trim(),
        },
      },
      {
        onSuccess: () => {
          setTimeout(reset, 1500);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
      }}
      title="Accept allocation"
      size="md"
    >
      {ioi && (
        <div className="space-y-4">
          <div className="text-xs font-sans text-tertiary">
            Accepting will create a binding ParticipationCommitment for the
            offered amount. Once accepted, the commitment is locked and CRT is
            committed against your reserve.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-sans">
            <div>
              <div className="text-tertiary">Offered amount</div>
              <div className="text-primary font-mono tabular-nums">
                {formatCurrencyFull(ioi.allocatedAmount)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Indicated amount</div>
              <div className="text-primary font-mono tabular-nums">
                {formatCurrencyFull(ioi.indicatedAmount)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Allocation expires</div>
              <div className="text-primary">
                {formatDate(ioi.allocationExpiresAt)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Offering</div>
              <div className="text-primary font-mono tabular-nums">
                {ioi.offeringId.slice(0, 8)}...
              </div>
            </div>
          </div>

          {blocked ? (
            /* Eligibility gate: the server refused to bind at the allocated
               amount. Render the computed HARD reasons and offer the audited
               override attestation path. */
            <div className="space-y-3">
              <div className="bg-error/5 border border-error/20 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
                  <span className="text-xs font-sans font-semibold text-error">
                    Blocked by the eligibility gate
                  </span>
                </div>
                <ul className="space-y-1">
                  {blocked.reasons.map((r, i) => (
                    <li key={i} className="text-xs font-sans text-secondary">
                      <span className="font-semibold">{ELIGIBILITY_SEVERITY_LABEL[r.severity]}:</span> {r.message}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-xs font-sans text-tertiary">
                Your institution may still bind this commitment under an
                audited override: the attestation below and your written
                justification are recorded verbatim on the audit trail as the
                decision record. Aaim provides the gate; your institution
                makes the credit and regulatory decision.
              </div>
              <Textarea
                label="Written justification (minimum 20 characters)"
                value={overrideJustification}
                onChange={setOverrideJustification}
                placeholder="e.g. Board resolution 2026-14 authorizes participations under this framework; profile data correction pending."
                rows={3}
              />
              <Checkbox
                checked={overrideAcknowledged}
                onChange={(checked) => setOverrideAcknowledged(checked)}
                label="I attest that my institution has reviewed the eligibility reasons above and elects to proceed."
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={reset}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleOverrideConfirm}
                  disabled={mutation.isPending || !overrideAcknowledged || !justificationValid}
                >
                  {mutation.isPending ? 'Accepting...' : 'Accept with Override'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {mutation.isError && (
                <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
                  <div className="text-xs font-sans text-error">
                    {mutation.error instanceof Error
                      ? mutation.error.message
                      : 'Allocation accept failed'}
                  </div>
                </div>
              )}

              {mutation.isSuccess && (
                <div className="bg-growth/10 border border-growth/30 rounded-sm p-3 text-xs font-sans text-secondary">
                  Allocation accepted. The binding Commitment has been created.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={reset}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Accepting...' : 'Confirm Accept'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Decline dialog
// ---------------------------------------------------------------------------

function DeclineDialog({
  ioi,
  open,
  onClose,
}: {
  ioi: ParticipationIoi | null;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useDeclineIoi();

  function reset() {
    setReason('');
    mutation.reset();
    onClose();
  }

  function handleConfirm() {
    if (!ioi) return;
    mutation.mutate(
      { ioiId: ioi.ioiId, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          setTimeout(reset, 1500);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
      }}
      title="Decline allocation"
      size="md"
    >
      {ioi && (
        <div className="space-y-4">
          <div className="text-xs font-sans text-tertiary">
            Declining will release the offered allocation back to the lead. No
            commitment is created. The IOI transitions to DECLINED.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-sans">
            <div>
              <div className="text-tertiary">Offered amount</div>
              <div className="text-primary font-mono tabular-nums">
                {formatCurrencyFull(ioi.allocatedAmount)}
              </div>
            </div>
            <div>
              <div className="text-tertiary">Allocation expires</div>
              <div className="text-primary">
                {formatDate(ioi.allocationExpiresAt)}
              </div>
            </div>
          </div>

          <div>
            <FormLabel htmlFor="decline-reason">
              Decline reason (optional)
            </FormLabel>
            <Input
              id="decline-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              placeholder="e.g. credit committee declined due to concentration"
            />
            <FormHelperText>
              Surfaced to the lead arranger for order-book intelligence.
            </FormHelperText>
          </div>

          {mutation.isError && (
            <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
              <div className="text-xs font-sans text-error">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Decline failed'}
              </div>
            </div>
          )}

          {mutation.isSuccess && (
            <div className="bg-warning/10 border border-warning/30 rounded-sm p-3 text-xs font-sans text-secondary">
              Allocation declined.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={reset}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Declining...' : 'Confirm Decline'}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Indications() {
  const navigate = useNavigate();
  const [acceptTarget, setAcceptTarget] = useState<ParticipationIoi | null>(
    null,
  );
  const [declineTarget, setDeclineTarget] = useState<ParticipationIoi | null>(
    null,
  );

  // Persona gate. Only FI operator roles carry a participantId resolvable
  // by CRT and can therefore hold IOIs. Platform admins (aaim_super_admin /
  // platform_admin / e2e_test_admin) intentionally do not enable the
  // /iois/me query -- the route would 401 for them and the auth middleware
  // would log the entire portal out. hasAnyRole normalizes both sides to
  // lowercase per .claude/rules/authz-case-insensitive.md.
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const isFiPersona = hasAnyRole([...FI_PERSONA_ROLES]);

  const { data, isLoading, error } = useMyIois(isFiPersona);
  const iois: ParticipationIoi[] = data?.iois ?? [];

  const submittedCount = iois.filter((i) => i.status === 'SUBMITTED').length;
  const offeredCount = iois.filter(
    (i) => i.status === 'ALLOCATION_OFFERED',
  ).length;
  const acceptedCount = iois.filter((i) => i.status === 'ACCEPTED').length;

  const columns: ColumnDef<ParticipationIoi, unknown>[] = [
    {
      id: 'offering',
      header: 'Offering',
      cell: ({ row }) => (
        <div>
          <div className="text-sm font-sans font-medium text-primary leading-normal">
            {row.original.institutionName ?? 'Indicated participant'}
          </div>
          <div className="text-xs font-mono text-tertiary tabular-nums leading-normal">
            {row.original.offeringId.slice(0, 8)}...
          </div>
        </div>
      ),
      size: 220,
    },
    {
      accessorKey: 'indicatedAmount',
      header: 'Indicated',
      cell: ({ getValue }) => (
        <span className="text-sm font-mono text-primary tabular-nums leading-normal">
          {formatCurrencyFull(getValue() as number)}
        </span>
      ),
      size: 150,
    },
    {
      accessorKey: 'allocatedAmount',
      header: 'Allocated',
      cell: ({ getValue }) => {
        const allocated = getValue() as number | undefined;
        return (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {allocated !== undefined ? formatCurrencyFull(allocated) : '--'}
          </span>
        );
      },
      size: 150,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={getIoiStatusVariant(row.original.status)} size="sm">
          {IOI_STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
      size: 180,
    },
    {
      accessorKey: 'allocationExpiresAt',
      header: 'Expires',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans text-secondary leading-normal">
          {formatDate(getValue() as string | undefined)}
        </span>
      ),
      size: 130,
    },
    {
      accessorKey: 'submittedAt',
      header: 'Submitted',
      cell: ({ getValue }) => (
        <span className="text-sm font-sans text-secondary leading-normal">
          {formatDate(getValue() as string | undefined)}
        </span>
      ),
      size: 130,
    },
    {
      id: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const ioi = row.original;
        if (ioi.status === 'ALLOCATION_OFFERED') {
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setAcceptTarget(ioi)}
              >
                Accept
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDeclineTarget(ioi)}
              >
                Decline
              </Button>
            </div>
          );
        }
        return (
          <span className="text-xs font-sans text-tertiary leading-normal">
            {ioi.status === 'SUBMITTED'
              ? 'Awaiting allocation'
              : ioi.status === 'ACCEPTED'
                ? 'Commitment created'
                : ioi.status === 'DECLINED'
                  ? 'Declined'
                  : ioi.status === 'WITHDRAWN'
                    ? 'Withdrawn'
                    : ioi.status === 'EXPIRED'
                      ? 'Expired'
                      : '--'}
          </span>
        );
      },
      size: 240,
    },
  ];

  // Platform-admin and other non-FI personas: render an honest informational
  // state. The /iois/me query stays disabled (see useMyIois enabled flag) so
  // there is no 401 from CRT, no auth-bounce, and no fabricated empty data
  // grid. Operators are pointed to the org switcher for tenant impersonation,
  // which is the real remediation path that already exists in PortalLayout.
  if (!isFiPersona) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            {/* Page title lives in the sticky topnav (AppHeader); the body opens
                with the descriptive subtitle, no redundant H1. */}
            <p className="text-sm font-sans text-tertiary">
              Indications of interest in the Loan Participations network.
            </p>
          </div>
        </div>

        <Card className="p-8">
          <div className="max-w-2xl">
            <Heading level={2} size="card" className="mb-3">
              No IOI book for platform operators
            </Heading>
            <p className="text-sm font-sans text-secondary leading-relaxed mb-3">
              Indications of Interest are held by FI participants in the Loan
              Participations network. Platform operators have no participant
              identity in CRT and therefore no IOI book of their own.
            </p>
            <p className="text-sm font-sans text-secondary leading-relaxed">
              To review a tenant&apos;s indications of interest, switch into a
              tenant session using the organization switcher in the top bar.
              The tenant view presents that FI&apos;s submitted, offered, and
              accepted indications.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Indications of interest you have submitted across the Loan
            Participations network. Accept or decline allocated offers from
            this view.
          </p>
        </div>
      </div>

      {error && (
        <ReadErrorBanner
          error={error}
          subject="indications of interest"
          surface="Loan Participations -- Indications of Interest"
        />
      )}

      <MetricCardGrid className="mb-6">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))
        ) : error ? (
          // A failed read has no counts: each tile withholds its value and
          // says why, never a measured 0 / $0 over an empty list.
          <>
            <MetricCard label="Total Indications of Interest" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Submitted" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Offered" value="--" caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Accepted" value="--" caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Indications of Interest"
              value={iois.length.toString()}
              change={`${formatCurrency(
                // indicatedAmount is required by the ParticipationIoi contract.
                iois.reduce((s, i) => s + i.indicatedAmount, 0),
              )} indicated`}
              changeType="neutral"
            />
            <MetricCard
              label="Submitted"
              value={submittedCount.toString()}
              change="Awaiting lead allocation"
              changeType="neutral"
            />
            <MetricCard
              label="Offered"
              value={offeredCount.toString()}
              change="Awaiting your decision"
              changeType={offeredCount > 0 ? 'positive' : 'neutral'}
            />
            <MetricCard
              label="Accepted"
              value={acceptedCount.toString()}
              change="Binding commitments created"
              changeType="neutral"
            />
          </>
        )}
      </MetricCardGrid>

      {isLoading ? (
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </Card>
      ) : error ? null : iois.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-sm font-sans text-secondary mb-3">
            You have no indications of interest.
          </div>
          <Button variant="primary" onClick={() => navigate('/syndication/marketplace')}>
            Browse marketplace
          </Button>
        </Card>
      ) : (
        <DataGrid<ParticipationIoi>
          data={iois}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          getRowId={(row) => row.ioiId}
          className="w-full rounded-sm shadow-sm"
        />
      )}

      <AcceptDialog
        ioi={acceptTarget}
        open={!!acceptTarget}
        onClose={() => setAcceptTarget(null)}
      />
      <DeclineDialog
        ioi={declineTarget}
        open={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
      />
    </div>
  );
}
